import { isHotkey, isTextEntryTarget } from '@tunasong/models'
import { useEffect } from 'react'

export interface PerformanceHotkeys {
  /** R: open the part menu */
  onPartMenu: () => void
  /** Shift+N: turn my notes on or off */
  onToggleNotes: () => void
  /** N: write a note on the section in view */
  onNote: () => void
}

/** Matched by `key`, not key code, so the shortcut follows the letter on any keyboard layout. */
const isPartMenuKey = isHotkey('r', { byKey: true })
const isToggleNotesKey = isHotkey('shift+n', { byKey: true })
const isNoteKey = isHotkey('n', { byKey: true })

/**
 * A key pressed while writing (an input, a textarea, the note composer or any editable surface), or
 * inside an open dialog or menu, belongs to that control, not to the performance view.
 */
const belongsToControl = (target: EventTarget | null) => {
  if (isTextEntryTarget(target)) {
    return true
  }
  if (!(target instanceof Element)) {
    return false
  }
  if (target instanceof HTMLElement && target.isContentEditable) {
    return true
  }
  return Boolean(target.closest('[contenteditable="true"], [role="dialog"], [role="menu"]'))
}

/**
 * Keyboard shortcuts for the performance view. Registered while the view is mounted, so they are not
 * global: R opens the part menu, Shift+N turns notes on or off, N writes a note.
 */
export const usePerformanceHotkeys = ({ onPartMenu, onToggleNotes, onNote }: PerformanceHotkeys) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || belongsToControl(event.target)) {
        return
      }
      if (isPartMenuKey(event)) {
        event.preventDefault()
        onPartMenu()
        return
      }
      if (isToggleNotesKey(event)) {
        event.preventDefault()
        onToggleNotes()
        return
      }
      if (isNoteKey(event)) {
        event.preventDefault()
        onNote()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onPartMenu, onToggleNotes, onNote])
}
